import { useEffect, useRef, useState } from "react";
import { Dial } from "@keydials/ui";
import { arcPath, type DialLayout, type DialModel } from "@keydials/dial-renderer";
import { motionFrame, sameStructure } from "./wheelMotion";

const MOTION_MS = 280;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The model to draw right now (KEY-560): the target itself, or a frame on the way to it when sections or action points were added,
 * removed or reordered. A rename or a colour changes at once. Anyone who prefers less motion, or a page with no animation frames,
 * gets the target straight away.
 */
export function useWheelMotion(target: DialModel, durationMs = MOTION_MS): DialModel {
  const [shown, setShown] = useState(target);
  const shownRef = useRef(target);

  useEffect(() => {
    const from = shownRef.current;
    const canAnimate = typeof requestAnimationFrame === "function" && !prefersReducedMotion();
    if (!canAnimate || sameStructure(from, target)) {
      shownRef.current = target;
      setShown(target);
      return;
    }

    let frame = 0;
    let startedAt: number | null = null;
    const tick = (now: number) => {
      startedAt ??= now;
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const next = progress >= 1 ? target : motionFrame(from, target, progress);
      shownRef.current = next;
      setShown(next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);

  return shown;
}

export interface BuilderWheelProps {
  /** The dial as it's being built: `definitionToDialModel` of the draft. */
  model: DialModel;
  /** What's selected, for `<Dial>`'s pair selection. */
  selection: { sectionKey: string; segmentKey?: string | null } | null;
  /** What the dial calls an action point, for screen readers. */
  segmentWord: string;
  /** The ghost wedges' words, from the app's copy. */
  labels: { addFirstSection: string; addSegment: string };
  onSelectSection: (sectionKey: string) => void;
  onSelectSegment: (sectionKey: string, segmentKey: string) => void;
  onAddFirstSection: () => void;
  onAddSegment: (sectionKey: string) => void;
  ariaLabel?: string;
}

/**
 * The wheel as the editor (KEY-560, the user app's builder). Tap a section or an action point to select it; everything outside the
 * selection fades. Where there's nothing yet, a dashed ghost wedge offers the next step: the first section on an empty wheel, and an
 * action point inside an empty section. Additions and removals reflow with motion.
 */
export function BuilderWheel({
  model,
  selection,
  segmentWord,
  labels,
  onSelectSection,
  onSelectSegment,
  onAddFirstSection,
  onAddSegment,
  ariaLabel,
}: BuilderWheelProps) {
  const shown = useWheelMotion(model);
  // Ghosts follow the target, not the frame: a section that has just been given its first action point stops offering one at once.
  const emptySections = new Set(model.sections.filter((s) => s.segments.length === 0).map((s) => s.key));

  return (
    <div className="kd-builder-wheel">
      <Dial
        model={shown}
        fluid
        selection={selection}
        dimUnselected
        segmentWord={segmentWord}
        ariaLabel={ariaLabel}
        onSelectSection={onSelectSection}
        onSelectSegment={onSelectSegment}
        overlay={(layout) => (
          <Ghosts
            layout={layout}
            model={shown}
            showFirstSection={model.sections.length === 0}
            emptySections={emptySections}
            labels={labels}
            onAddFirstSection={onAddFirstSection}
            onAddSegment={onAddSegment}
          />
        )}
      />
    </div>
  );
}

function Ghosts({
  layout,
  model,
  showFirstSection,
  emptySections,
  labels,
  onAddFirstSection,
  onAddSegment,
}: {
  layout: DialLayout;
  model: DialModel;
  showFirstSection: boolean;
  emptySections: ReadonlySet<string>;
  labels: BuilderWheelProps["labels"];
  onAddFirstSection: () => void;
  onAddSegment: (sectionKey: string) => void;
}) {
  if (showFirstSection) {
    return (
      <GhostWedge
        d={arcPath(model.innerRadius, model.outerRadius, 0, Math.PI * 2)}
        label={labels.addFirstSection}
        textAt={[0, -(model.innerRadius + model.outerRadius) / 2]}
        onActivate={onAddFirstSection}
      />
    );
  }

  const radius = (model.innerRadius + model.midRadius) / 2;
  return (
    <>
      {layout.sectionArcs
        .filter((arc) => emptySections.has(arc.sectionKey))
        .map((arc) => {
          const mid = (arc.startAngle + arc.endAngle) / 2;
          const roomForWords = (arc.endAngle - arc.startAngle) * radius > 110;
          return (
            <GhostWedge
              key={arc.sectionKey}
              d={arcPath(model.innerRadius, model.midRadius, arc.startAngle, arc.endAngle)}
              label={labels.addSegment}
              textAt={roomForWords ? [Math.sin(mid) * radius, -Math.cos(mid) * radius] : null}
              onActivate={() => onAddSegment(arc.sectionKey)}
            />
          );
        })}
    </>
  );
}

function GhostWedge({ d, label, textAt, onActivate }: { d: string; label: string; textAt: [number, number] | null; onActivate: () => void }) {
  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={label}
      className="kd-builder-ghost"
      style={{ cursor: "pointer" }}
      onClick={onActivate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActivate();
        }
      }}
    >
      <path d={d} style={{ fill: "var(--accent-soft, rgba(30, 29, 81, 0.06))", stroke: "var(--text-faint, #8b94a8)" }} strokeWidth={1.5} strokeDasharray="7 6" />
      {textAt && (
        <text
          x={textAt[0]}
          y={textAt[1]}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={17}
          fontWeight={600}
          pointerEvents="none"
          style={{ fill: "var(--text-muted, #5b6478)" }}
        >
          {label}
        </text>
      )}
    </g>
  );
}
